// src/features/notifications/EventRulesEditor.tsx
//
// Per-event-type notification overrides — a list of {event_type, enabled,
// channels, send_immediately} rows. Shared by two RPC pairs that use the
// identical EventNotificationRule shape:
//   - GetUserSettingsEvent / UpdateUserSettingsEvent (UserPreferencesPage,
//     "override how I get notified for this one event type")
//   - GetTeamMemberOverrides / UpdateTeamMemberOverrides (TeamSettingsPage,
//     "this one member's personal overrides within the team")

import { useState } from 'react';
import { useI18n } from '@react/hooks';
import { Button } from '@react/components';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { NotificationChannelTypeValues } from '@core/types/notifications/NotificationChannelType';
import type { NotificationChannelType } from '@core/types/notifications/NotificationChannelType';
import { formatChannelType } from './logic';
import type { EventNotificationRule } from '@core/types/notifications/EventNotificationRule';

export interface EventRulesEditorProps {
  rules: EventNotificationRule[];
  onChange: (rules: EventNotificationRule[]) => void;
}

export function EventRulesEditor({ rules, onChange }: EventRulesEditorProps) {
  const { t } = useI18n();
  const [newEventType, setNewEventType] = useState('');

  const updateRule = (idx: number, patch: Partial<EventNotificationRule>) => {
    onChange(rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const toggleChannel = (idx: number, channelType: NotificationChannelType) => {
    const rule = rules[idx];
    const items = rule.channel_settings?.items ?? [];
    const exists = items.find(c => c.channel_type === channelType);
    const nextItems = exists
      ? items.map(c => (c.channel_type === channelType ? { ...c, enabled: !c.enabled } : c))
      : [...items, { channel_type: channelType, enabled: true, specific_contact_ids: [], custom_template_id: '' }];
    updateRule(idx, { channel_settings: { items: nextItems } });
  };

  const removeRule = (idx: number) => {
    onChange(rules.filter((_, i) => i !== idx));
  };

  const addRule = () => {
    if (!newEventType.trim()) return;
    onChange([
      ...rules,
      { event_type: newEventType.trim(), enabled: true, channel_settings: { items: [] }, send_immediately: true },
    ]);
    setNewEventType('');
  };

  return (
    <div className="space-y-3">
      {rules.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('notifications.eventRules.empty')}</p>
      ) : (
        rules.map((rule, idx) => {
          const channelItems = rule.channel_settings?.items ?? [];
          return (
            <div key={idx} className="rounded-lg border border-border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm font-medium text-foreground">{rule.event_type}</span>
                <button
                  type="button"
                  onClick={() => removeRule(idx)}
                  className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                  title={t('common.remove')}
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <ToggleSwitch
                label={t('notifications.eventRules.enabled')}
                checked={rule.enabled}
                onChange={(v) => updateRule(idx, { enabled: v })}
              />
              <ToggleSwitch
                label={t('notifications.eventRules.sendImmediately')}
                checked={rule.send_immediately}
                onChange={(v) => updateRule(idx, { send_immediately: v })}
              />
              <div className="flex flex-wrap gap-2">
                {NotificationChannelTypeValues.filter(v => v !== 'NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED').map(ch => {
                  const chInfo = formatChannelType(ch);
                  const isOn = channelItems.find(c => c.channel_type === ch)?.enabled ?? false;
                  return (
                    <button
                      key={ch}
                      type="button"
                      onClick={() => toggleChannel(idx, ch)}
                      className={`rounded-md border px-2 py-1 text-xs font-medium transition-colors ${
                        isOn ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border text-muted-foreground'
                      }`}
                    >
                      {t(chInfo.labelKey)}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
      <div className="flex items-center gap-2 border-t border-border pt-2">
        <input
          value={newEventType}
          onChange={e => setNewEventType(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addRule();
            }
          }}
          placeholder={t('notifications.eventRules.newEventTypePlaceholder')}
          aria-label={t('notifications.eventRules.newEventType')}
          autoCapitalize="off"
          className="flex-1 rounded-md border border-dashed border-input bg-transparent px-2 py-1 font-mono text-sm text-foreground focus:outline-none"
        />
        <Button size="sm" variant="secondary" onClick={addRule} disabled={!newEventType.trim()}>
          {t('notifications.eventRules.addOverride')}
        </Button>
      </div>
    </div>
  );
}

export default EventRulesEditor;
